import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback, useState } from "react";
import { TextRenderer } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/RichText/TextRenderer";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultPrimaryStyles } from "./defaultPrimaryStyles";
import { defaultSecondaryStyles } from "./defaultSecondaryStyles";

interface ButtonStylesSet {
  default: Record<string, StyleValue>;
  hover: Record<string, StyleValue>;
  margin: StyleValue;
  padding: StyleValue;
  sizing: Record<string, StyleValue>;
}

interface ButtonStyles {
  custom: ButtonStylesSet;
  primary: ButtonStylesSet;
  secondary: ButtonStylesSet;
}

type ButtonType = "primary" | "secondary" | "custom";

interface ButtonProps extends ComponentPropsWithRef<"a"> {
  blockPath: string;
  buttonType: ButtonType;
  "data-preview-mode": boolean;
  href: string;
  styles: ButtonStyles;
  text: string;
}

const CLASS_MAP = {
  primary: [
    "wb-pt-button-primary-top wb-pb-button-primary-bottom wb-pl-button-primary-left wb-pr-button-primary-right",
    "wb-text-button-primary wb-font-button-primary wb-leading-button-primary wb-tracking-button-primary wb-text-center",
    "wb-bg-button-primary wb-text-button-primary-text wb-border-button-primary",
    "wb-rounded-tl-button-primary-default-left-top wb-rounded-tr-button-primary-default-right-top wb-rounded-bl-button-primary-default-left-bottom wb-rounded-br-button-primary-default-right-bottom",
    "wb-shadow-button-primary",
    "hover:wb-bg-button-primary-hover hover:wb-text-button-primary-text-hover",
    "hover:wb-border-button-primary-hover",
    "hover:wb-rounded-tl-button-primary-hover-left-top hover:wb-rounded-tr-button-primary-hover-right-top hover:wb-rounded-bl-button-primary-hover-left-bottom hover:wb-rounded-br-button-primary-hover-right-bottom",
    "hover:wb-font-button-primary-hover hover:wb-leading-button-primary-hover hover:wb-tracking-button-primary-hover",
    "hover:wb-shadow-button-primary-hover",
  ],
  secondary: [
    "wb-pt-button-secondary-top wb-pb-button-secondary-bottom wb-pl-button-secondary-left wb-pr-button-secondary-right",
    "wb-text-button-secondary wb-font-button-secondary wb-leading-button-secondary wb-tracking-button-secondary wb-text-center",
    "wb-bg-button-secondary wb-text-button-secondary-text wb-border-button-secondary",
    "wb-rounded-tl-button-secondary-default-left-top wb-rounded-tr-button-secondary-default-right-top wb-rounded-bl-button-secondary-default-left-bottom wb-rounded-br-button-secondary-default-right-bottom",
    "wb-shadow-button-secondary",
    "hover:wb-bg-button-secondary-hover hover:wb-text-button-secondary-text-hover",
    "hover:wb-border-button-secondary-hover",
    "hover:wb-rounded-tl-button-secondary-hover-left-top hover:wb-rounded-tr-button-secondary-hover-right-top hover:wb-rounded-bl-button-secondary-hover-left-bottom hover:wb-rounded-br-button-secondary-hover-right-bottom",
    "hover:wb-font-button-secondary-hover hover:wb-leading-button-secondary-hover hover:wb-tracking-button-secondary-hover",
    "hover:wb-shadow-button-secondary-hover",
  ],
};

export const Button = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      text,
      blockPath,
      href,
      styles,
      className,
      buttonType,
      onClick,
      target = "_self",
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    const isPrimary = buttonType === "primary";
    const isSecondary = buttonType === "secondary";

    const styleSet = isPrimary
      ? styles.primary
      : isSecondary
        ? styles.secondary
        : styles.custom;

    const currentStyles = isHovered ? styleSet.hover : styleSet.default;

    const fallbackSet = isPrimary
      ? defaultPrimaryStyles
      : defaultSecondaryStyles;

    const inlineStyles = {
      ...parseStyles({
        styles: currentStyles,
        defaultStyles: isHovered ? fallbackSet.hover : fallbackSet.default,
      }),
      ...parseStyles({
        styles: styleSet.sizing,
        defaultStyles: fallbackSet.sizing,
      }),
      ...parseStyles({
        styles: { margin: styleSet.margin },
        defaultStyles: { margin: fallbackSet.margin },
      }),
      ...parseStyles({
        styles: { padding: styleSet.padding },
        defaultStyles: { padding: fallbackSet.padding },
      }),
    };

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreviewMode) event.preventDefault();
        onClick?.(event);
      },
      [isPreviewMode, onClick],
    );

    const buttonClass =
      buttonType === "primary" ? CLASS_MAP.primary : CLASS_MAP.secondary;

    return (
      <a
        ref={ref}
        className={classNames(
          "wb-inline-flex wb-min-h-fit wb-min-w-fit wb-items-center wb-justify-center wb-transition-colors",
          buttonClass,
          className,
        )}
        style={inlineStyles}
        href={href}
        target={target}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        rel="noreferrer noopener"
        {...rest}
      >
        <TextRenderer blockPath={blockPath} text={text} />
      </a>
    );
  },
);

Button.displayName = "Button";
